class CarouselPagedDots extends HTMLElement {
  connectedCallback() {
    const section = this.closest('[data-testid="carousel-section"]');
    this.slidesContainer = section?.querySelector('slideshow-slides');
    if (!this.slidesContainer) return;

    // Componente padre con el método select() (lo que usaba on:click="/select/N")
    this.slideshowComponent =
      this.slidesContainer.closest('slideshow-component') ||
      section.querySelector('slideshow-component');

    this.totalSlides = this.slidesContainer.children.length;
    this.currentLeftmostSlide = 0;
    this.lastPerView = this.getSlidesPerView();
    this.render();

    // IntersectionObserver: detecta qué slides son visibles.
    // Funciona con scroll nativo, transform, o cualquier mecanismo de movimiento.
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target._isVisible = entry.intersectionRatio > 0.5;
        });
        const slides = Array.from(this.slidesContainer.children);
        const firstVisible = slides.findIndex((s) => s._isVisible);
        if (firstVisible >= 0 && firstVisible !== this.currentLeftmostSlide) {
          this.currentLeftmostSlide = firstVisible;
          this.updateActive();
        }
      },
      { root: this.slidesContainer, threshold: 0.5 }
    );

    Array.from(this.slidesContainer.children).forEach((slide) => {
      this.intersectionObserver.observe(slide);
    });

    // Re-renderiza al cruzar breakpoint (mobile ↔ desktop)
    this.resizeObserver = new ResizeObserver(() => {
      const perView = this.getSlidesPerView();
      if (perView !== this.lastPerView) {
        this.lastPerView = perView;
        this.render();
      }
    });
    this.resizeObserver.observe(this.slidesContainer);
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  getSlidesPerView() {
    const slides = this.slidesContainer.children;
    if (!slides.length) return 1;
    const containerWidth = this.slidesContainer.clientWidth;
    const slideWidth = slides[0].getBoundingClientRect().width;
    if (slideWidth <= 0) return 1;
    const gap = parseFloat(getComputedStyle(this.slidesContainer).gap) || 0;
    return Math.max(1, Math.round((containerWidth + gap) / (slideWidth + gap)));
  }

  // Única fuente de verdad de los límites de página.
  // La última página se clampa al final (maxStart) para quedar flush a la derecha,
  // de modo que puede solaparse con la anterior. goToPage y updateActive usan ESTO.
  getPageStarts() {
    const perView = this.getSlidesPerView();
    const totalPages = Math.max(1, Math.ceil(this.totalSlides / perView));
    const maxStart = Math.max(0, this.totalSlides - perView);
    return Array.from({ length: totalPages }, (_, i) =>
      Math.min(i * perView, maxStart)
    );
  }

  render() {
    const starts = this.getPageStarts();
    const totalPages = starts.length;

    this.style.display = totalPages <= 1 ? 'none' : '';

    this.innerHTML = '';
    for (let i = 0; i < totalPages; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'carousel-dot';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', `Página ${i + 1} de ${totalPages}`);
      btn.dataset.page = i;
      btn.addEventListener('click', () => this.goToPage(i));
      this.appendChild(btn);
    }
    this.updateActive();
  }

  goToPage(pageIndex) {
    const starts = this.getPageStarts();
    const targetIndex = starts[pageIndex] ?? 0;
    const isLast = pageIndex === starts.length - 1;
    const canScroll =
      this.slidesContainer.scrollWidth > this.slidesContainer.clientWidth + 1;

    // Última página + scroll nativo: pegar al final para no dejar hueco.
    // (Si Horizon usa transform en vez de scroll, canScroll es false y caemos a select.)
    if (isLast && canScroll) {
      const maxScroll =
        this.slidesContainer.scrollWidth - this.slidesContainer.clientWidth;
      this.slidesContainer.scrollTo({ left: maxScroll, behavior: 'smooth' });
      return;
    }

    // Camino preferido: API nativa de Horizon
    if (typeof this.slideshowComponent?.select === 'function') {
      this.slideshowComponent.select(targetIndex);
      return;
    }

    // Fallback: scroll nativo
    const target = this.slidesContainer.children[targetIndex];
    if (!target) return;
    const left = target.offsetLeft - this.slidesContainer.offsetLeft;
    this.slidesContainer.scrollTo({ left, behavior: 'smooth' });
  }

  updateActive() {
    const starts = this.getPageStarts();

    // Página activa = el mayor start que sea <= al slide más a la izquierda.
    // Esto respeta el clamp de la última página (ya no usamos floor(leftmost/perView)).
    let activePage = 0;
    starts.forEach((start, i) => {
      if (this.currentLeftmostSlide >= start) activePage = i;
    });

    Array.from(this.children).forEach((dot, i) => {
      const active = i === activePage;
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
      dot.classList.toggle('is-active', active);
    });
  }
}

customElements.define('carousel-paged-dots', CarouselPagedDots);